import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { first, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { BdoApiService } from '../../services/bdo-api.service';
import { ReBaseData } from '../../../shared/utils/re-base-data';
import { CustomerStoreService } from '../../services/customer-store.service';
import { LOADING_STATE } from '../../enums/loadingState.enum';
import { LoginService } from '../../../login/login.service';
import { StorageService } from '../../services/storage.service';
import { Account, AccountInfo, Address, AddressData } from '../../../../assets/js/com/ts_api_client';
import { TranslateService } from '@ngx-translate/core';
import { Utilities } from '../../../shared/utils/utilities';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';

export interface AccountWithAddress {
  accountId: string;
  displayAddress: string;
}

@Component({
    selector: 'bdo-contract-overview',
    templateUrl: './contract-overview.component.html',
    styleUrls: ['./contract-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractOverviewComponent implements OnInit {
  public selectedAccount: AccountWithAddress;
  public state: LOADING_STATE = LOADING_STATE.IDLE;
  public LoadingState = LOADING_STATE;
  public errorMessage: string = null;
  public address: AddressData;
  public canAddTariff: boolean = true;
  public Utilities = Utilities;
  public urlDivision: string = '';

  protected reBaseData = ReBaseData;

  constructor(
    private apiService: BdoApiService,
    private customerStore: CustomerStoreService,
    private loginService: LoginService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toastService: ToastService
  ) {
    this.urlDivision = this.activatedRoute.snapshot.queryParams['division'];
  }

  ngOnInit(): void {
    // Cleanup data from contract processes to prevent conflicting data states. TODO: General Guard
    StorageService.clearChargeFlexData();

    this.state = LOADING_STATE.LOADING;
    const selectedAccountId = this.customerStore.getAccountId();

    this.loginService.isLoggedIn$.pipe(
      first(),
    ).subscribe({ next: (loggedIn) => {
      if (loggedIn && selectedAccountId && this.customerStore.currentAccountIdIsInactive() === false) {
        this.loggedInFlow(selectedAccountId);
      } else {
        // no selected account id
        this.state = LOADING_STATE.ERROR;
        this.errorMessage = this.translateService.instant('contract.overview.error.noActiveAccount');
      }
    } });
  }

  setSelectedAccountWithAddress(accountId: string, address: Address) {
    this.address = {
      cityName: address.street?.city?.name,
      houseNum: address.houseNum,
      postCode: address.street?.city?.postCode,
      streetName: address.street?.name
    };
    this.selectedAccount = {
      'accountId': accountId,
      'displayAddress': address.street.name + ' ' + address.houseNum + ', ' +
        address.street.city.postCode + ' ' + address.street.city.name
    };
  }

  private loggedInFlow(selectedAccountId: string) {
    if (this.urlDivision) {
      // Find the account whose AccountInfo contains the requested division, then redirect if needed
      this.apiService.getAccounts().pipe(
        first(),
        switchMap((accounts: Account[]) => {
          if (accounts.length > 3) { // avoid N+1 API calls for high-account customers as the division will probably be ambiguous anyway
            return of(null);
          }
          const accountInfoRequests = accounts.map(account =>
            this.apiService.getAccountInfo(account.accountId)
          );
          return forkJoin(accountInfoRequests);
        })
      ).subscribe({
        next: (accountInfoList: AccountInfo[] | null) => {
          const matchingInfos = accountInfoList?.filter(info =>
            info.divisions?.some(d => d.name === this.urlDivision)
          ) ?? [];
          if (matchingInfos.length === 0 && accountInfoList !== null) {
            this.router.navigate(['/anmelden']);
            return;
          }
          if (accountInfoList === null || matchingInfos.length > 1) {
            // Too many accounts or ambiguous division match — fall back to dashboard
            this.toastService.pushToast({
              type: 'warning',
              headline: this.translateService.instant('contract.overview.error.ambiguousDivisionTitle'),
              content: this.translateService.instant('contract.overview.error.ambiguousDivisionContent'),
              durationIndicator: false
            });
            this.router.navigate(['/dashboard']);
            return;
          }
          const matchingInfo = matchingInfos[0];
          if (matchingInfo.accountId !== selectedAccountId) {
            this.customerStore.setAccountId(matchingInfo.accountId!);
            this.router.navigate(['/vertrag', matchingInfo.accountId], {
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
            // Load data directly — the component may be reused after navigation
            // and ngOnInit would not fire again
            this.loadAccountInfo(matchingInfo.accountId!);
          } else {
            // Division already matches current account — proceed normally
            this.loadAccountInfo(selectedAccountId);
          }
        },
        error: () => {
          this.state = LOADING_STATE.ERROR;
          this.errorMessage = this.translateService.instant('general.error.error');
        }
      });
    } else {
      // No division filter — proceed normally
      this.loadAccountInfo(selectedAccountId);
    }
  }

  private loadAccountInfo(accountId: string) {
    this.apiService.getAccountInfo(accountId)
      .subscribe({
        next: (resAccountInfo: AccountInfo) => {
          this.setSelectedAccountWithAddress(accountId, resAccountInfo.address);
          this.state = LOADING_STATE.IDLE;
        },
        error: () => {
          this.state = LOADING_STATE.ERROR;
          this.errorMessage = this.translateService.instant('general.error.error');
        }
      });
  }



}
